import { useRef, useState } from 'react'
import { Link } from 'react-router'
import {
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from '#app/components/ui/sidebar'
import { cn } from '#app/utils/misc'
import { Badge } from './ui/badge'
import { UnifiedIcon, type UnifiedIconName } from './ui/unified-icon'

type NavChild = {
	name: string
	url: string
	isActive?: boolean
	icon?: UnifiedIconName
	/** Icon shown when the child is the active route; falls back to `icon`. */
	activeIcon?: UnifiedIconName
}

type NavItem = {
	icon: UnifiedIconName
	/** Icon shown when the row is the active route; falls back to `icon`. */
	activeIcon?: UnifiedIconName
	isActive?: boolean
	name: string
	primary?: boolean
	url: string
	badge?: string | number
	comingSoon?: boolean
	children?: NavChild[]
}

function Chevron({ open }: { open: boolean }) {
	return (
		<svg
			viewBox="0 0 24 24"
			width="14"
			height="14"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			className={cn(
				'shrink-0 text-[#9CA3AF] transition-transform duration-200 group-data-[collapsible=icon]:hidden',
				open && 'rotate-90',
			)}
		>
			<path d="M9 6l6 6-6 6" />
		</svg>
	)
}

function NavRow({ item }: { item: NavItem }) {
	const children = item.children ?? []
	// Active rows show their `activeIcon` variant (falls back to `icon`).
	const iconName =
		item.isActive && item.activeIcon ? item.activeIcon : item.icon
	// Accordion: parents with children toggle their sub-menu. Default open when
	// the current route is already within this section (item.isActive).
	const [open, setOpen] = useState(Boolean(item.isActive))
	// Auto-open when navigating INTO the section (isActive false → true) while
	// still allowing manual collapse. Render-time prop-change sync (no useEffect).
	const wasActive = useRef(Boolean(item.isActive))
	if (item.isActive && !wasActive.current) setOpen(true)
	wasActive.current = Boolean(item.isActive)

	// When a section near the bottom of the scrollable nav is expanded, its
	// sub-menu can render below the fold. After opening, scroll the row (now
	// taller, including its children) into view so the options are visible.
	const rowRef = useRef<HTMLLIElement>(null)
	const toggle = (e: React.MouseEvent) => {
		// Toggle the accordion instead of navigating — the first child
		// (e.g. "Overview") is the section's landing route.
		e.preventDefault()
		const willOpen = !open
		setOpen(willOpen)
		if (willOpen) {
			// rAF: wait for the expanded children to commit/lay out before scrolling.
			requestAnimationFrame(() => {
				rowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
			})
		}
	}

	if (children.length > 0) {
		return (
			<SidebarMenuItem ref={rowRef}>
				<SidebarMenuButton
					asChild
					isActive={item.isActive}
					tooltip={item.name}
					className={cn(item.isActive ? 'sidebar-active' : '')}
				>
					<Link
						to={item.url}
						onClick={toggle}
						aria-expanded={open}
						className="flex w-full items-center justify-between"
					>
						<div className="flex items-center gap-2">
							<UnifiedIcon
								className="shrink-0"
								name={iconName}
								size="md"
							/>
							<span className="text-xs font-normal text-[#000000] group-data-[collapsible=icon]:hidden">
								{item.name}
							</span>
						</div>
						<Chevron open={open} />
					</Link>
				</SidebarMenuButton>
				{open ? (
					// Nest children under the parent label: the vertical guide line sits
					// at the icon centre (ml-9 = 36px) and child text aligns with the
					// parent label start (px-6 24 + size-6 icon 24 + gap-2 8 = 56px).
					<SidebarMenuSub className="mx-0 ml-10 translate-x-0 gap-1 border-[#E9F2F9] px-0 pl-3 group-data-[collapsible=icon]:hidden">
						{children.map((child) => {
							
							const childIcon =
							 child.icon
							return (
								<SidebarMenuSubItem key={child.url}>
									<SidebarMenuSubButton
										asChild
										size="sm"
										isActive={child.isActive}
									>
										<Link to={child.url} className="flex items-center gap-2">
											{childIcon ? (
												<UnifiedIcon
													className="size-4.5 shrink-0"
													name={childIcon}
												/>
											) : null}
											{child.name}
										</Link>
									</SidebarMenuSubButton>
								</SidebarMenuSubItem>
							)
						})}
					</SidebarMenuSub>
				) : null}
			</SidebarMenuItem>
		)
	}

	return (
		<SidebarMenuItem>
			<SidebarMenuButton
				asChild
				isActive={item.isActive}
				tooltip={item.name}
				className={cn(item.isActive ? 'sidebar-active' : ' ')}
			>
				<Link
					to={
						item.comingSoon
							? `/coming-soon?page=${encodeURIComponent(item.name)}`
							: item.url
					}
					className={cn(
						'flex w-full items-center justify-between',
						item.comingSoon && 'cursor-not-allowed opacity-60',
					)}
				>
					<div className="flex items-center gap-2">
						<UnifiedIcon
							className="shrink-0 font-normal"
							name={iconName}
							size="md"
						/>
						<span
							className={cn(
								'text-xs font-normal text-[#000000] group-data-[collapsible=icon]:hidden',
								item.comingSoon && 'text-[#32302F]',
							)}
						>
							{item.name}
						</span>
					</div>
					{item.badge && !item.comingSoon && (
						<Badge
							variant={item.isActive ? 'default' : 'secondary'}
							className={cn(
								'ml-auto text-xs group-data-[collapsible=icon]:hidden',
								item.isActive && 'bg-primary text-white',
							)}
						>
							{item.badge}
						</Badge>
					)}
				</Link>
			</SidebarMenuButton>
		</SidebarMenuItem>
	)
}

export function NavItems({
	items,
	...props
}: React.ComponentPropsWithoutRef<typeof SidebarGroup> & {
	items: NavItem[]
}) {
	return (
		<SidebarGroup {...props}>
			<SidebarGroupContent className="border-[#E9F2F9]">
				<SidebarMenu className="sidebar-menu-list">
					{items.map((item) => (
						<NavRow key={item.name} item={item} />
					))}
				</SidebarMenu>
			</SidebarGroupContent>
		</SidebarGroup>
	)
}
